import { useQuery, useQueryClient } from "@tanstack/react-query";
import { useState, type FormEvent } from "react";
import { Navigate, useNavigate, useSearchParams } from "react-router";
import { describeError, isApiError } from "@/api/client";
import { login, sessionQuery } from "@/api/session";
import { Button, InlineAlert, TextField } from "@/components";
import { useDocumentTitle } from "@/lib/hooks";
import { BrandMark } from "../layout/BrandMark";
import styles from "./auth.module.css";

/** Only same-app paths are allowed as a post-login destination. */
export function safeNextPath(next: string | null): string {
  if (!next || !next.startsWith("/") || next.startsWith("//") || next.startsWith("/login")) return "/";
  return next;
}

export function LoginPage() {
  useDocumentTitle("Sign in");
  const queryClient = useQueryClient();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const next = safeNextPath(searchParams.get("next"));
  const session = useQuery({ ...sessionQuery, retry: false });

  const [username, setUsername] = useState("");
  const [password, setPassword] = useState("");
  const [pending, setPending] = useState(false);
  const [error, setError] = useState<string | null>(null);

  if (session.isSuccess) return <Navigate to={next} replace />;

  async function handleSubmit(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    setPending(true);
    setError(null);
    try {
      await login(queryClient, username.trim(), password);
      await navigate(next, { replace: true });
    } catch (caught) {
      setError(
        isApiError(caught, 401)
          ? "The username or password is wrong. Check both and try again."
          : describeError(caught),
      );
      setPending(false);
    }
  }

  return (
    <main className={styles.loginPage}>
      <div className={styles.loginPanel}>
        <div className={styles.brand}>
          <BrandMark />
          <span className={styles.brandName}>Aldehyde</span>
        </div>
        <h1 className={styles.loginTitle}>Sign in</h1>
        <form className={styles.loginForm} onSubmit={handleSubmit} noValidate>
          {error ? <InlineAlert tone="danger" title={error} /> : null}
          <TextField
            label="Username"
            name="username"
            autoComplete="username"
            autoCapitalize="off"
            spellCheck={false}
            required
            value={username}
            onValueChange={setUsername}
          />
          <TextField
            label="Password"
            name="password"
            type="password"
            autoComplete="current-password"
            required
            value={password}
            onValueChange={setPassword}
          />
          <Button
            type="submit"
            variant="primary"
            size="lg"
            fullWidth
            pending={pending}
            pendingLabel="Signing in…"
            disabled={!username.trim() || !password}
          >
            Sign in
          </Button>
        </form>
      </div>
    </main>
  );
}
